import { useState, useEffect } from 'react';
import { Navigation } from '../../components/Navigation';
import { SubscriptionManager } from '../../components/SubscriptionManager';
import { useAuthStore } from '../../store/authStore';
import { useCreditsStore } from '../../store/creditsStore';
import { supabase } from '../../lib/supabase';
import { formatPhoneForDisplay } from '../../lib/phoneValidation';
import {
  Mail,
  Lock,
  Save,
  AlertCircle,
  Check,
  Eye,
  EyeOff,
  Coins,
  History,
  CreditCard,
  Shield,
  Phone,
  CheckCircle,
  LogOut,
} from 'lucide-react';

interface CreditTransaction {
  id: string;
  amount: number;
  type: string;
  description: string;
  created_at: string;
}

export function Settings() {
  const { user, profile, signOut } = useAuthStore();
  const { balance, fetchCredits } = useCreditsStore();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [transactionPage, setTransactionPage] = useState(1);
  const transactionsPerPage = 10;

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      fetchCredits(profile!.id);
      loadTransactions();
    }
  }, [user, profile]);

  useEffect(() => {
    validateEmail(email);
  }, [email]);

  useEffect(() => {
    validateNewPassword(newPassword);
  }, [newPassword]);

  useEffect(() => {
    validateConfirmPassword(confirmPassword);
  }, [confirmPassword, newPassword]);

  const loadTransactions = async () => {
    if (!profile) return;

    setLoadingTransactions(true);
    try {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setTransactions(data || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      setEmailError('Email is required');
    } else if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
    } else if (!value.includes('.')) {
      setEmailError('Email must contain a domain extension (e.g., .com)');
    } else {
      setEmailError('');
    }
  };

  const validateNewPassword = (value: string) => {
    if (value && value.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
    } else if (value && !/(?=.*[a-z])(?=.*[A-Z])/.test(value)) {
      setPasswordError('Password must contain both uppercase and lowercase letters');
    } else if (value && !/(?=.*\d)/.test(value)) {
      setPasswordError('Password must contain at least one number');
    } else {
      setPasswordError('');
    }
  };

  const validateConfirmPassword = (value: string) => {
    if (value && value !== newPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleUpdateEmail = async () => {
    if (emailError || !email) {
      setMessage({ type: 'error', text: 'Please fix email errors before updating' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({
        email: email,
      });

      if (error) throw error;

      await supabase
        .from('profiles')
        .update({ email: email })
        .eq('id', profile!.id);

      setMessage({ type: 'success', text: 'Email updated successfully! Check your new email for verification.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update email' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'Please fill in all password fields' });
      return;
    }

    if (passwordError || confirmPasswordError) {
      setMessage({ type: 'error', text: 'Please fix password errors before updating' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update password' });
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const digitsOnly = input.replace(/\D/g, '');

    let formatted = digitsOnly;
    if (digitsOnly.length >= 6) {
      formatted = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    } else if (digitsOnly.length >= 3) {
      formatted = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    }

    setPhoneNumber(formatted);
  };

  const handleSendOTP = async () => {
    if (!phoneNumber) {
      setMessage({ type: 'error', text: 'Please enter a phone number' });
      return;
    }

    setSendingOtp(true);
    setMessage(null);

    try {
      const { sendOTPToPhone } = useAuthStore.getState();
      const result = await sendOTPToPhone(phoneNumber);

      if (result.success) {
        setMessage({ type: 'success', text: 'OTP sent successfully! Check your phone for the code.' });
        setShowOtpInput(true);
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to send OTP' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to send OTP' });
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode) {
      setMessage({ type: 'error', text: 'Please enter the OTP code' });
      return;
    }

    setVerifyingOtp(true);
    setMessage(null);

    try {
      const { verifyPhoneOTP } = useAuthStore.getState();
      const result = await verifyPhoneOTP(phoneNumber, otpCode);

      if (result.success) {
        setMessage({ type: 'success', text: 'Phone number verified successfully!' });
        setShowOtpInput(false);
        setPhoneNumber('');
        setOtpCode('');
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to verify OTP' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to verify OTP' });
    } finally {
      setVerifyingOtp(false);
    }
  };

  const paginatedTransactions = transactions.slice(
    (transactionPage - 1) * transactionsPerPage,
    transactionPage * transactionsPerPage
  );

  const totalPages = Math.ceil(transactions.length / transactionsPerPage);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Settings</h1>
          <p className="text-gray-600">Manage your account settings and preferences</p>
        </div>

        {message && (
          <div
            className={`mb-6 p-4 rounded-xl border-2 ${
              message.type === 'success'
                ? 'bg-green-50 border-green-300 text-green-900'
                : 'bg-red-50 border-red-300 text-red-900'
            }`}
          >
            <div className="flex items-start gap-3">
              {message.type === 'success' ? (
                <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              )}
              <p className="font-semibold">{message.text}</p>
            </div>
          </div>
        )}

        {profile && (
          <div className="mb-8">
            <SubscriptionManager userId={profile.id} />
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Coins className="w-8 h-8" />
              <h3 className="text-lg font-bold">Credit Balance</h3>
            </div>
            <div className="text-4xl font-bold mb-3">{balance}</div>
            <p className="text-white/80 text-sm mb-4">Available credits</p>
            <a
              href="/jobseeker/credits"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-orange-600 rounded-lg font-semibold hover:bg-gray-100 transition text-sm"
            >
              <Coins className="w-4 h-4" />
              Get More Credits
            </a>
          </div>

          <div className="bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <History className="w-8 h-8" />
              <h3 className="text-lg font-bold">Total Transactions</h3>
            </div>
            <div className="text-4xl font-bold mb-1">{transactions.length}</div>
            <p className="text-white/80 text-sm">Credit activities</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8" />
              <h3 className="text-lg font-bold">Account Status</h3>
            </div>
            <div className="text-2xl font-bold mb-1">Active</div>
            <p className="text-white/80 text-sm">Good standing</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-cyan-100 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-cyan-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Email Settings</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition ${
                    emailError
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-gray-300 focus:border-cyan-500'
                  }`}
                  placeholder="your@email.com"
                />
                {emailError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {emailError}
                  </p>
                )}
              </div>

              <button
                onClick={handleUpdateEmail}
                disabled={loading || !!emailError || email === user?.email}
                className="w-full px-6 py-3 bg-cyan-500 text-white rounded-xl font-bold hover:bg-cyan-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                Update Email
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Lock className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Password Settings</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none pr-12"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none pr-12 transition ${
                      passwordError
                        ? 'border-red-500 focus:border-red-600'
                        : 'border-gray-300 focus:border-cyan-500'
                    }`}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {passwordError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {passwordError}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none pr-12 transition ${
                      confirmPasswordError
                        ? 'border-red-500 focus:border-red-600'
                        : 'border-gray-300 focus:border-cyan-500'
                    }`}
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPasswordError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {confirmPasswordError}
                  </p>
                )}
              </div>

              <button
                onClick={handleUpdatePassword}
                disabled={
                  loading ||
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword ||
                  !!passwordError ||
                  !!confirmPasswordError
                }
                className="w-full px-6 py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Lock className="w-5 h-5" />
                Update Password
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Phone Number</h2>
            </div>

            {profile?.phone_verified ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border-2 border-green-300 rounded-xl">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-bold text-green-900 mb-1">Phone Verified</p>
                      <p className="text-green-700">
                        {formatPhoneForDisplay(profile.phone_number || '')}
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  You can now use your phone number to sign in to your account.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Add and verify your phone number to enable phone-based login.
                </p>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <div className="flex gap-2">
                    <div className="w-20 px-3 py-3 border-2 border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-medium flex items-center justify-center">
                      +1
                    </div>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                      placeholder="(313) 758-7217"
                      maxLength={14}
                      disabled={showOtpInput}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Enter your US phone number
                  </p>
                </div>

                {!showOtpInput ? (
                  <button
                    onClick={handleSendOTP}
                    disabled={sendingOtp || !phoneNumber}
                    className="w-full px-6 py-3 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    {sendingOtp ? 'Sending OTP...' : 'Send Verification Code'}
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Verification Code
                      </label>
                      <input
                        type="text"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none text-center text-2xl font-bold tracking-widest"
                        placeholder="000000"
                        maxLength={6}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Enter the 6-digit code sent to your phone
                      </p>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleVerifyOTP}
                        disabled={verifyingOtp || otpCode.length !== 6}
                        className="flex-1 px-6 py-3 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-5 h-5" />
                        {verifyingOtp ? 'Verifying...' : 'Verify Code'}
                      </button>
                      <button
                        onClick={() => {
                          setShowOtpInput(false);
                          setOtpCode('');
                        }}
                        disabled={verifyingOtp}
                        className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>

                    <button
                      onClick={handleSendOTP}
                      disabled={sendingOtp}
                      className="w-full text-sm text-green-600 hover:text-green-700 font-medium underline"
                    >
                      Resend Code
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <History className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Credit Transaction History</h2>
          </div>

          {loadingTransactions ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg font-semibold">No transactions yet</p>
              <p className="text-gray-500 text-sm mt-2">Your credit activity will appear here</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-bold text-gray-700">Date</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-700">Type</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-700">Description</th>
                      <th className="text-right py-3 px-4 font-bold text-gray-700">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTransactions.map((transaction) => (
                      <tr key={transaction.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(transaction.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              transaction.amount > 0
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {transaction.amount > 0 ? 'Earned' : 'Spent'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">{transaction.description}</td>
                        <td
                          className={`py-3 px-4 text-right text-sm font-bold ${
                            transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {transaction.amount > 0 ? '+' : ''}
                          {transaction.amount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => setTransactionPage(Math.max(1, transactionPage - 1))}
                    disabled={transactionPage === 1}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-gray-700 font-semibold">
                    Page {transactionPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setTransactionPage(Math.min(totalPages, transactionPage + 1))}
                    disabled={transactionPage === totalPages}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sign Out Section */}
        <div className="bg-white rounded-2xl shadow-lg border-2 border-red-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <LogOut className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Sign Out</h2>
          </div>

          <p className="text-gray-600 mb-6">
            Sign out of your account. You'll need to sign back in to access your profile.
          </p>

          <button
            onClick={async () => {
              await signOut();
              window.location.href = '/login';
            }}
            className="w-full px-6 py-4 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition flex items-center justify-center gap-3 shadow-lg"
          >
            <LogOut className="w-6 h-6" />
            Sign Out of ResuSwipe
          </button>
        </div>
      </div>
    </div>
  );
}
